import { Button } from '@/components/ui/button';
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Gift, User, FileText, Users } from 'lucide-react';

import { format } from 'date-fns';
import { vi } from 'date-fns/locale';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import TextUserInactive from '@/components/TextUserInactive';
import TextInActive from '@/components/TextInActive';
import {
  GiftRequestDTO,
  useSendToManagerMarketing,
} from '@/hooks/useGiftsRequests';
import StatusBadge from '@/components/StatusBadge';
import LoadingCircle from '@/components/LoadingCircle';
import { GiftRequestStatus } from '@/api/gifts-requests';
import { formatCurrencyWithoutSymbol } from '@/lib/utils';

interface ICustomer {
  id: string;
  fullName: string;
}

function DetailGiftRequests({ giftRequest }: { giftRequest: GiftRequestDTO }) {
  const [open, setOpen] = useState(false);
  const { mutateAsync: sendToManagerMarketing, isPending } =
    useSendToManagerMarketing();

  // Format date if available
  const formatDate = (dateString: string) => {
    if (!dateString) return '—';
    return format(new Date(dateString), 'dd/MM/yyyy', { locale: vi });
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
    }).format(amount);
  };

  const handleSendToManagerMarketing = async () => {
    await sendToManagerMarketing(giftRequest.id);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="p-0 bg-transparent border-none shadow-none group"
        >
          <div className="relative inline-flex items-center gap-2 bg-gradient-to-r from-amber-400 via-yellow-500 to-orange-400 px-2 py-1 rounded-2xl border-2 border-white shadow-xl text-white font-bold text-sm overflow-hidden before:absolute before:inset-0 before:bg-gradient-to-r before:from-white/20 before:via-transparent before:to-white/20 before:translate-x-[-100%] group-hover:before:translate-x-[100%] before:transition-transform before:duration-1000 hover:shadow-amber-500/50 hover:border-amber-200 transition-all duration-300 cursor-pointer transform group-hover:scale-105 group-hover:shadow-2xl">
            <div className="relative z-10 flex items-center gap-2">
              <div className="w-1.5 h-1.5 bg-white rounded-full opacity-90"></div>
              <span className="font-extrabold tracking-wider drop-shadow-sm">
                ADYC
              </span>
              <div className="w-px h-4 bg-white/40"></div>
              <span className="font-mono font-bold tracking-wide">
                {giftRequest.id}
              </span>
            </div>
          </div>
        </Button>
      </DialogTrigger>
      <DialogContent className="min-w-[1000px] max-h-[95vh] overflow-y-auto luxury-theme">
        <DialogDescription className="sr-only">
          Chi tiết yêu cầu tặng quà
        </DialogDescription>
        <DialogHeader className="luxury-header">
          <DialogTitle className="luxury-title">
            Thông tin chi tiết yêu cầu tặng quà
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-8">
          {/* Thông tin quà tặng */}
          <Card className="luxury-card luxury-theme">
            <CardHeader className="pb-2 border-b border-luxury-100">
              <CardTitle className="text-lg flex items-center text-luxury-900">
                <Gift className="w-5 h-5 mr-3 text-luxury-600" />
                Thông tin quà tặng
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex flex-col space-y-2">
                  <span className="luxury-label">Tên quà tặng</span>
                  <span className="font-medium text-lg text-luxury-800">
                    {giftRequest.gift.name}
                    <TextInActive status={giftRequest.gift.status} />
                  </span>
                </div>
                <div className="flex flex-col space-y-2">
                  <span className="luxury-label">Số lượng quà còn lại</span>
                  <span className="font-medium text-luxury-800">
                    {formatCurrencyWithoutSymbol(
                      giftRequest.gift.remainingQuantity
                    )}
                  </span>
                </div>

                <div className="flex flex-col space-y-2">
                  <span className="luxury-label">Đơn giá</span>
                  <span className="font-medium text-luxury-800">
                    {formatCurrency(Number(giftRequest.gift.unitPrice))}
                  </span>
                </div>

                <div className="flex flex-col space-y-2">
                  <span className="luxury-label">
                    Số lượng quà trên mỗi khách hàng
                  </span>
                  <span className="font-medium text-luxury-800">
                    {giftRequest.numberOfGiftsPerCustomer} quà x{' '}
                    {giftRequest.customers.length} khách hàng ={' '}
                    {giftRequest.numberOfGiftsPerCustomer *
                      giftRequest.customers.length}{' '}
                    quà
                  </span>
                </div>

                <div className="flex flex-col space-y-2">
                  <span className="luxury-label">Tổng giá trị (VNĐ)</span>
                  <span className="font-medium text-luxury-800 whitespace-pre-line break-words">
                    {formatCurrency(
                      giftRequest.numberOfGiftsPerCustomer *
                        giftRequest.gift.unitPrice *
                        giftRequest.customers.length
                    )}
                  </span>
                </div>
                <div className="flex flex-col space-y-2">
                  <span className="luxury-label">Lý do</span>
                  <span className="font-medium text-luxury-800 whitespace-pre-line break-words">
                    {giftRequest.reason}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Thông tin khách hàng */}
          <Card className="luxury-card luxury-theme">
            <CardHeader className="border-b border-luxury-100">
              <CardTitle className="text-lg flex items-center text-luxury-900">
                <Users className="w-5 h-5 mr-3 text-luxury-600" />
                Thông tin khách hàng
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {giftRequest.customers.map((customer: ICustomer) => (
                  <div
                    key={customer.id}
                    className="p-4 border border-luxury-200 rounded-lg bg-luxury-50"
                  >
                    <div className="flex flex-col space-y-2">
                      <div className="flex items-center space-x-2">
                        <span className="luxury-label">Mã khách hàng:</span>
                        <span className="font-medium text-luxury-800">
                          KH-{customer.id}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="luxury-label">Họ và tên:</span>
                        <span className="font-medium text-luxury-800">
                          {customer.fullName}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Thông tin nhân viên */}
          <Card className="luxury-card luxury-theme">
            <CardHeader className="pb-2 border-b border-luxury-100">
              <CardTitle className="text-lg flex items-center text-luxury-900">
                <User className="w-5 h-5 mr-3 text-luxury-600" />
                Thông tin nhân viên
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex flex-col space-y-2">
                  <span className="luxury-label">Người tạo yêu cầu</span>
                  <span className="font-medium text-luxury-800">
                    {giftRequest.createdBy.username}
                    <TextUserInactive status={giftRequest.createdBy.status} />
                  </span>
                </div>

                <div className="flex flex-col space-y-2">
                  <span className="luxury-label">Người phụ trách</span>
                  <span className="font-medium text-luxury-800">
                    {giftRequest.assignedTo.username}
                    <TextUserInactive status={giftRequest.assignedTo.status} />
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Thông tin hệ thống */}
          <Card className="luxury-card luxury-theme">
            <CardHeader className="pb-2 border-b border-luxury-100">
              <CardTitle className="text-lg flex items-center text-luxury-900">
                <FileText className="w-5 h-5 mr-3 text-luxury-600" />
                Thông tin hệ thống
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex flex-col space-y-2">
                  <span className="luxury-label">Trạng thái</span>
                  <StatusBadge status={giftRequest.status} />
                </div>

                <div className="flex flex-col space-y-2">
                  <span className="luxury-label">ID</span>
                  <span className="font-medium text-sm font-mono bg-luxury-50 p-1 rounded border border-luxury-100 text-luxury-800">
                    {giftRequest.id}
                  </span>
                </div>

                <div className="flex flex-col space-y-2">
                  <span className="luxury-label">Ngày tạo</span>
                  <span className="font-medium text-luxury-800">
                    {formatDate(giftRequest.createdAt)}
                  </span>
                </div>

                <div className="flex flex-col space-y-2">
                  <span className="luxury-label">Cập nhật lần cuối</span>
                  <span className="font-medium text-luxury-800">
                    {formatDate(giftRequest.updatedAt)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
          {giftRequest.status === GiftRequestStatus.NEW && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                className="luxury-button-primary"
                onClick={handleSendToManagerMarketing}
                disabled={isPending}
              >
                {isPending ? <LoadingCircle /> : 'Gửi yêu cầu'}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default DetailGiftRequests;
